import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FileText,
  Users,
  UserPlus,
  ArrowUpRight,
  Loader2,
} from "lucide-react";
import { useAdminAuth, API_BASE } from "@/admin/AdminAuthContext";

const StatCard = ({ icon: Icon, label, value, to, testid, loading }) => (
  <Link
    to={to}
    data-testid={testid}
    className="group relative bg-[#150A1F] border border-[#2A0A4A] rounded-sm p-7 hover:border-[#E6D5B8]/40 hover:-translate-y-0.5 transition-all duration-300 flex flex-col"
  >
    <div className="flex items-center justify-between mb-7">
      <div className="w-11 h-11 flex items-center justify-center bg-[#3D1565]/25 border border-[#3D1565] rounded-sm">
        <Icon size={18} className="text-[#E6D5B8]" strokeWidth={1.6} />
      </div>
      <ArrowUpRight
        size={16}
        className="text-[#E6D5B8] opacity-0 group-hover:opacity-100 transition-opacity"
      />
    </div>
    <div className="text-[10px] tracking-luxury uppercase text-[#A397B4]">
      {label}
    </div>
    <div className="font-display text-5xl font-light text-[#F9F6F0] mt-2 leading-none flex items-center gap-3">
      {loading ? (
        <Loader2 size={20} className="animate-spin text-[#A397B4]" />
      ) : (
        value
      )}
    </div>
  </Link>
);

const AdminDashboard = () => {
  const { authedAxios } = useAdminAuth();
  const [stats, setStats] = useState({
    leads: null,
    applications: null,
    posts: null,
  });
  const [latestLead, setLatestLead] = useState(null);
  const [latestApp, setLatestApp] = useState(null);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      authedAxios.get(`${API_BASE}/leads?limit=100`).catch(() => ({ data: [] })),
      authedAxios
        .get(`${API_BASE}/applications?limit=100`)
        .catch(() => ({ data: [] })),
      authedAxios.get(`${API_BASE}/posts?limit=100`).catch(() => ({ data: [] })),
    ]).then(([leads, apps, posts]) => {
      if (!mounted) return;
      setStats({
        leads: leads.data.length,
        applications: apps.data.length,
        posts: posts.data.length,
      });
      setLatestLead(leads.data[0] || null);
      setLatestApp(apps.data[0] || null);
    });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="admin-dashboard" className="max-w-6xl mx-auto">
      <div className="mb-10">
        <div className="text-[11px] tracking-luxury uppercase text-[#E6D5B8] mb-3">
          Panel administrativo
        </div>
        <h1 className="font-display text-4xl lg:text-5xl font-light leading-tight">
          Resumen de{" "}
          <span className="italic text-[#E6D5B8]">EMB</span>
        </h1>
      </div>

      <div className="grid sm:grid-cols-3 gap-5 mb-12">
        <StatCard
          icon={Users}
          label="Leads recibidos"
          value={stats.leads ?? "—"}
          to="/admin/leads"
          testid="stat-leads"
          loading={stats.leads === null}
        />
        <StatCard
          icon={UserPlus}
          label="Aplicaciones"
          value={stats.applications ?? "—"}
          to="/admin/applications"
          testid="stat-applications"
          loading={stats.applications === null}
        />
        <StatCard
          icon={FileText}
          label="Insights publicados"
          value={stats.posts ?? "—"}
          to="/admin/posts"
          testid="stat-posts"
          loading={stats.posts === null}
        />
      </div>

      {/* Latest activity */}
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-[#150A1F] border border-[#2A0A4A] rounded-sm p-7">
          <div className="flex items-center justify-between mb-5">
            <div className="text-[10px] tracking-luxury uppercase text-[#E6D5B8]">
              Último lead
            </div>
            <Link
              to="/admin/leads"
              className="text-[10px] tracking-luxury uppercase text-[#A397B4] hover:text-[#E6D5B8]"
            >
              Ver todos →
            </Link>
          </div>
          {latestLead ? (
            <div data-testid="latest-lead">
              <div className="font-display text-xl mb-1">{latestLead.name}</div>
              <div className="text-xs text-[#A397B4]">
                {latestLead.email} · {latestLead.phone}
              </div>
              <div className="text-[11px] tracking-luxury uppercase text-[#E6D5B8] mt-3">
                Interés: {latestLead.interest}
              </div>
              {latestLead.message && (
                <p className="text-sm text-[#F9F6F0]/70 mt-3 line-clamp-3 font-light">
                  {latestLead.message}
                </p>
              )}
            </div>
          ) : (
            <div className="text-sm text-[#F9F6F0]/45 italic">
              Sin leads aún.
            </div>
          )}
        </div>

        <div className="bg-[#150A1F] border border-[#2A0A4A] rounded-sm p-7">
          <div className="flex items-center justify-between mb-5">
            <div className="text-[10px] tracking-luxury uppercase text-[#E6D5B8]">
              Última aplicación
            </div>
            <Link
              to="/admin/applications"
              className="text-[10px] tracking-luxury uppercase text-[#A397B4] hover:text-[#E6D5B8]"
            >
              Ver todas →
            </Link>
          </div>
          {latestApp ? (
            <div data-testid="latest-application">
              <div className="font-display text-xl mb-1">{latestApp.name}</div>
              <div className="text-xs text-[#A397B4]">
                {latestApp.city} · {latestApp.email}
              </div>
              <div className="text-[11px] tracking-luxury uppercase text-[#E6D5B8] mt-3">
                Cédula: {latestApp.cedula_status} · Experiencia:{" "}
                {latestApp.experience}
              </div>
            </div>
          ) : (
            <div className="text-sm text-[#F9F6F0]/45 italic">
              Sin aplicaciones aún.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
